"use client";
import { SearchInput } from "@gettactic/components/src/controls/SearchInput/SearchInput";
import { ApprovableCell } from "@/features/permissions/components/ApprovableCell";
import { PermissionCell } from "@/features/permissions/components/PermissionCell";
import { PermissionsDatesCell } from "@/features/permissions/components/PermissionsDatesCell";
import { TooltipProvider } from "@gettactic/components/src/primitives/tooltip";
import {
  ConfirmOperationModal,
  IResourcePermissionWithResName,
  groupSharedKey
} from "@/features/reserve/components/resources/tabs/access-restrictions/RestrictionsTab";
import {
  accessAssignmentDefaultGroup,
  accessAssignmentGroups
} from "@/features/reserve/components/resources/tabs/access-restrictions/defines";
import { api } from "@/lib/api/api";
import { IResourcePermission } from "@gettactic/api";
import {
  actionDelete,
  DataMan,
  DataManAction,
  DataManColumDef,
  DataManGroupDef
} from "@gettactic/components/src/data-display/data-man";
import { getObjectKeys } from "@gettactic/helpers/src/objects/getObjectKeys";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

type Props = {
  className?: string;
  data: IResourcePermission[];
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
  inGroups: { [groupKey: string]: IResourcePermissionWithResName[] };
  onDeleted?: () => void;
};

// Helper to generate a unique key for bulk selection
function bulkKey(perm: IResourcePermission) {
  return `${perm.id}_${perm.resource_id}_${perm.user ? "user" : "team"}`;
}

export const SharedPermissionTable = ({
  className,
  data: rawData,
  setOperationalModal,
  inGroups,
  onDeleted
}: Props) => {
  const [query, setQuery] = useState("");
  const queryClient = useQueryClient();

  // Filter data based on search query and add group field
  const filteredData = useMemo(() => {
    const _query = query.toLowerCase().trim();
    const rawItems = [...rawData];
    const items = _query.length
      ? rawItems.filter((x) =>
          x.user
            ? x.user.name.toLowerCase().includes(_query) ||
              x.user.email.toLowerCase().includes(_query)
            : x.team?.name.toLowerCase().includes(_query)
        )
      : rawItems;

    return items.map((item) => ({
      ...item,
      group: item.access_group_type || accessAssignmentDefaultGroup,
      // Use bulkKey as the id for selection since original id may conflict
      _bulkKey: bulkKey(item)
    }));
  }, [query, rawData]);

  // Convert accessAssignmentGroups to DataManGroupDef array
  const groups: DataManGroupDef[] = useMemo(() => {
    return getObjectKeys(accessAssignmentGroups).map((key, index) => {
      const group = accessAssignmentGroups[key];
      return {
        id: key,
        label: group.label,
        shortLabel: group.shortLabel,
        color: group.color,
        order: index
      };
    });
  }, []);

  // Define columns for DataMan
  const columns: DataManColumDef<IResourcePermission & { group: string }>[] =
    useMemo(
      () => [
        {
          key: "assignment",
          title: "Assignment",
          render: (data) => <PermissionCell permission={data} />
        },
        {
          key: "dates",
          title: "Dates",
          width: 160,
          render: (data) => <PermissionsDatesCell permission={data} />
        },
        {
          key: "is_approvable",
          width: 140,
          title: "Approval Required",
          info: "Users will need a manager to approve a reservation before it's accepted by the system.",
          render: (data) => {
            // @ts-expect-error Id is a numeric type so it shouldn't be used with this special string value
            if (data.id === "*") return null;
            return (
              <ApprovableCell
                permission={data}
                onChange={(x: IResourcePermission) => {
                  setOperationalModal(
                    x
                      ? {
                          operation: "is_approvable",
                          applyTo: inGroups[groupSharedKey(x)]
                        }
                      : null
                  );
                }}
              />
            );
          }
        }
      ],
      [inGroups, setOperationalModal]
    );

  // Define actions for DataMan
  const actions: DataManAction[] = useMemo(
    () => [
      actionDelete(
        async ({ target, targetRecords }) => {
          const deleteAll: IResourcePermission[] = [];
          const records = targetRecords.length > 0 ? targetRecords : [target];
          records.forEach((x: IResourcePermission) => {
            // Special "*" id is used as a marker for "all users" entries
            if (String(x.id) === "*") {
              deleteAll.push(x);
            } else {
              deleteAll.push(...(inGroups[groupSharedKey(x)] || [x]));
            }
          });

          let deleted = 0;
          let error = 0;

          for (const permission of deleteAll) {
            const res =
              await api.client.organizations.resourceScheduledPermissionsDelete(
                permission
              );
            if (res.error) {
              error++;
            } else {
              deleted++;
            }
          }

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: ["organizations.resourceScheduledPermissionsAll"]
            }),
            queryClient.invalidateQueries({
              queryKey: ["organizations.getResourceAdditionalAccess"]
            })
          ]);

          if (error > 0) {
            toast.error("Sorry we couldn't delete all the assignments");
          } else {
            toast.success(
              deleted === 1
                ? "Assignment deleted"
                : "Group of assignments deleted"
            );
            onDeleted?.();
          }
        },
        {
          allowBulk: true,
          confirm: {
            title: "Delete Assignment",
            message:
              "Are you sure you want to delete this assignment? This action cannot be undone."
          }
        }
      )
    ],
    [inGroups, queryClient, onDeleted]
  );

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4 px-1 sm:gap-16">
          <SearchInput query={query} setQuery={setQuery} />
        </div>
        <div className="px-1 relative">
          <DataMan
            data={filteredData}
            columns={columns}
            actions={actions}
            dataIdKey="_bulkKey"
            enableGroupBy
            groupByKey="group"
            groups={groups}
            allowBulkSelection
            actionsColumnWidth={50}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};
